import { Box, Card, CardContent, Chip, Stack, Typography } from "@mui/material";
import { AppColors } from "../../constants/AppColors";
import { BookingResponse } from "../../models/ResponseModels/BookingResponse";
import { UnitResponse } from "../../models/ResponseModels/UnitResponse";
import {
  formatCalendarRange,
  getAvailablePeriodsForUnit,
  getCalendarUnitAccent,
} from "../../utils/helperFunctions/calendarTimelineHelper";

type CalendarAvailablePeriodsCardProps = {
  t: (key: string, values?: Record<string, string | number>) => string;
  units: UnitResponse[];
  bookings: BookingResponse[];
  from: string;
  until: string;
};

const CalendarAvailablePeriodsCard = ({
  t,
  units,
  bookings,
  from,
  until,
}: CalendarAvailablePeriodsCardProps) => {
  return (
    <Card
      elevation={0}
      sx={{
        mt: 3,
        borderRadius: 4,
        border: `1px solid ${AppColors.border}`,
        bgcolor: AppColors.card,
      }}
    >
      <CardContent sx={{ p: { xs: 2, md: 3 } }}>
        <Stack
          direction="row"
          spacing={2}
          sx={{
            mb: 2,
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            rowGap: 1,
          }}
        >
          <Box>
            <Typography
              sx={{
                color: "#0f766e",
                fontWeight: 950,
                textTransform: "uppercase",
                fontSize: 11,
                letterSpacing: 0.7,
              }}
            >
              {t("calendar.availableDates")}
            </Typography>

            <Typography variant="h5" sx={{ fontWeight: 950 }}>
              {t("calendar.availableDatesByUnit")}
            </Typography>
          </Box>

          <Chip
            size="small"
            label={formatCalendarRange(from, until)}
            sx={{
              bgcolor: AppColors.surface,
              border: `1px solid ${AppColors.border}`,
              fontWeight: 900,
            }}
          />
        </Stack>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
            gap: 2,
          }}
        >
          {units.map((unit, index) => {
            const periods = getAvailablePeriodsForUnit(
              unit,
              bookings,
              from,
              until,
            );
            const firstPeriod = periods[0];
            const accent = getCalendarUnitAccent(unit, index);

            return (
              <Box
                key={unit.id}
                sx={{
                  borderRadius: 3,
                  border: `1px solid ${AppColors.border}`,
                  borderTop: `5px solid ${accent}`,
                  bgcolor: AppColors.surface,
                  px: 2,
                  py: 1.6,
                  boxShadow: "0 8px 18px rgba(15, 23, 42, 0.04)",
                }}
              >
                <Stack
                  direction="row"
                  spacing={2}
                  sx={{
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ alignItems: "center" }}
                    >
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: "999px",
                          bgcolor: accent,
                          flexShrink: 0,
                        }}
                      />

                      <Typography sx={{ fontWeight: 950 }}>
                        {unit.name}
                      </Typography>
                    </Stack>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.4, fontWeight: 750 }}
                    >
                      {firstPeriod
                        ? `${formatCalendarRange(
                            firstPeriod.start,
                            firstPeriod.end,
                          )} (${firstPeriod.nights} ${t(
                            "reservations.nights",
                          )})`
                        : t("calendar.noFreePeriods")}
                    </Typography>
                  </Box>

                  <Chip
                    size="small"
                    label={
                      periods.length > 0
                        ? `${periods.length} ${t("calendar.terms")}`
                        : t("calendar.closed")
                    }
                    sx={{
                      bgcolor: periods.length > 0 ? "#dcfce7" : "#ffedd5",
                      color: periods.length > 0 ? "#047857" : "#c2410c",
                      fontWeight: 950,
                      flexShrink: 0,
                    }}
                  />
                </Stack>
              </Box>
            );
          })}
        </Box>
      </CardContent>
    </Card>
  );
};

export default CalendarAvailablePeriodsCard;
